import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/card";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Input } from "ui/input";
import { Label } from "ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { Users, Plus, Search, Filter, X, Eye, Edit2 } from "lucide-react";
import {
  getEmployees,
  getDepartments,
  getVenues,
  DepartmentMasterApi,
  VenueApi,
} from "services/employeeService";
import { toast } from "sonner";
import Pagination from "ui/pagination";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface Employee {
  id: string;
  employeeNo: string;
  itsNo: string;
  name: string;
  email: string;
  hrType: string;
  status: string;
  roomNo: string;
  initials: string;
  avatarColor: string;
}

interface EmployeeListPageProps {
  onNavigate: (page: string, employeeId?: string) => void;
}

export function EmployeeListPage({ onNavigate }: EmployeeListPageProps) {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<DepartmentMasterApi[]>([]);
  const [venues, setVenues] = useState<VenueApi[]>([]);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    department: "all",
    status: "all",
    venue: "all",
    hrType: "all",
  });

  useEffect(() => {
    setPageHeader({
      title: "Employee Management",
      childTitle:"HR staff, departments, payroll and documents",
      icon: <Users className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const getAvatarColor = (text: string) => {
    const colors = [
      "#3B82F6", // blue
      "#EC4899", // pink
      "#10B981", // green
      "#8B5CF6", // purple
      "#F59E0B", // orange
      "#6366F1", // indigo
      "#EF4444", // red
      "#14B8A6", // teal
      "#F97316", // amber
      "#84CC16", // lime
      "#06B6D4", // cyan
      "#A855F7", // violet
    ];

    let hash = 0;

    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await getEmployees({
        searchText: searchTerm || null,

        departmentId:
          filters.department !== "all" ? Number(filters.department) : undefined,

        venueId: filters.venue !== "all" ? Number(filters.venue) : undefined,

        status: filters.status !== "all" ? filters.status : undefined,

        hrType: filters.hrType !== "all" ? filters.hrType : undefined,

        pageNo,
        pageSize,
      });

      const mappedEmployees: Employee[] = res.items.map((item) => ({
        id: item.id.toString(),
        employeeNo: item.employeeNo,
        itsNo: item.itsNo,
        name: item.fullName,
        email: item.email,
        hrType: item.hrType,
        status: item.status,
        roomNo: item.roomNo,

        initials: item.fullName
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase(),

        avatarColor: getAvatarColor(item.fullName),
      }));

      setEmployees(mappedEmployees);
      setTotalCount(res.totalCount);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to load employees";

      toast.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [departmentRes, venueRes] = await Promise.all([
        getDepartments(),
        getVenues(),
      ]);

      setDepartments(departmentRes);
      setVenues(venueRes);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load filter dropdowns");
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDropdownData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    pageNo,
    pageSize,
    filters.department,
    filters.status,
    filters.venue,
    filters.hrType,
  ]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-700 border border-green-300";
      case "inactive":
        return "bg-gray-100 text-gray-700 border border-gray-300";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  const getHRTypeBadgeClass = (hrType: string) => {
    switch (hrType) {
      case "Staff":
        return "bg-green-100 text-green-700 border border-green-300";
      case "Volunteer":
        return "bg-blue-100 text-blue-700 border border-blue-300";
      case "Mulla":
        return "bg-purple-100 text-purple-700 border border-purple-300";
      case "Contractor":
        return "bg-amber-100 text-amber-700 border border-amber-300";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex justify-end flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex ">
            <Button
              onClick={() => navigate("/employee-management/form")}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Employee
            </Button>
          </div>
        </div>

        {/* Search and Filter Toggle */}
        <Card className="bg-white border-[#5B9BD5]/30">
          <CardContent className="pt-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search name, ITS, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant={showFilters ? "default" : "outline"}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                style={showFilters ? { background: "#5B9BD5" } : {}}
                className={
                  showFilters
                    ? "text-white"
                    : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
                }
                title="Toggle Advanced Filters"
              >
                <Filter className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Filters Section */}
        {showFilters && (
          <Card
            className="bg-white border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter employees by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter-department" className="text-sm">
                    Department
                  </Label>
                  <Select
                    value={filters.department}
                    onValueChange={(value) => {
                      setPageNo(1);
                      setFilters({ ...filters, department: value });
                    }}
                  >
                    <SelectTrigger
                      id="filter-department"
                      className="h-9 text-sm"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>

                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.departmentName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => {
                      setPageNo(1);
                      setFilters({ ...filters, status: value });
                    }}
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-venue" className="text-sm">
                    Venue
                  </Label>
                  <Select
                    value={filters.venue}
                    onValueChange={(value) => {
                      setPageNo(1);
                      setFilters({ ...filters, venue: value });
                    }}
                  >
                    <SelectTrigger id="filter-venue" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>

                      {venues.map((venue) => (
                        <SelectItem
                          key={venue.venueId}
                          value={venue.venueId.toString()}
                        >
                          {venue.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-hrtype" className="text-sm">
                    HR Type
                  </Label>
                  <Select
                    value={filters.hrType}
                    onValueChange={(value) => {
                      setPageNo(1);
                      setFilters({ ...filters, hrType: value });
                    }}
                  >
                    <SelectTrigger id="filter-hrtype" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="Staff">Staff</SelectItem>
                      <SelectItem value="Volunteer">Volunteer</SelectItem>
                      <SelectItem value="Mulla">Mulla</SelectItem>
                      <SelectItem value="Contractor">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(filters.department !== "all" ||
                filters.status !== "all" ||
                filters.venue !== "all" ||
                filters.hrType !== "all") && (
                <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {filters.department !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Dept: {filters.department}
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                      {filters.venue !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Venue: {filters.venue}
                        </Badge>
                      )}
                      {filters.hrType !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Type: {filters.hrType}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFilters({
                        department: "all",
                        status: "all",
                        venue: "all",
                        hrType: "all",
                      })
                    }
                    className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Employee Table/Cards */}
        <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
          <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
            <CardTitle style={{ color: "#5B9BD5" }}>Employees</CardTitle>
            <CardDescription className="pb-6">
              {totalCount} employee{totalCount !== 1 ? "s" : ""} found
            </CardDescription>
          </CardHeader>
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Employee
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      ITS No
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      HR Type
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Status
                    </TableHead>
                    {/* <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Venue</TableHead>
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs">Dept</TableHead> */}
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-gray-500"
                      >
                        No employees found
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow key={emp.id} className="hover:bg-gray-50">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                              style={{ backgroundColor: emp.avatarColor }}
                            >
                              {emp.initials}
                            </div>
                            <div>
                              <div className="font-medium text-sm text-gray-900">
                                {emp.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {emp.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-xs font-mono text-[#5B9BD5]">
                          {emp.itsNo}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-xs">
                          <Badge
                            className={
                              getHRTypeBadgeClass(emp.hrType) + " text-xs px-2"
                            }
                          >
                            {emp.hrType}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-xs">
                          <Badge
                            className={
                              getStatusBadgeClass(emp.status) + " text-xs px-2"
                            }
                          >
                            {emp.status}
                          </Badge>
                        </TableCell>
                        {/* <TableCell className="px-4 py-3 text-xs text-gray-600">
                          {emp.venue} · {emp.subVenue}
                        </TableCell> */}
                        {/* <TableCell className="px-4 py-3 text-xs text-gray-600">{emp.department}</TableCell> */}
                        <TableCell className="px-4 py-3 text-xs text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/employee-management/view/${emp.employeeNo}`,
                                )
                              }
                              className="h-8 px-2 hover:bg-sky-50"
                            >
                              <Eye className="w-4 h-4 text-[#5B9BD5]" />
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate(
                                  `/employee-management/edit/${emp.employeeNo}`,
                                )
                              }
                              className="h-8 px-2 hover:bg-sky-50"
                            >
                              <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y">
              {employees.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No employees found
                </div>
              ) : (
                employees.map((emp) => (
                  <div key={emp.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3 mb-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                        style={{ backgroundColor: emp.avatarColor }}
                      >
                        {emp.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 mb-0.5">
                          {emp.name}
                        </div>
                        <div className="text-xs text-gray-500 mb-1 truncate">
                          {emp.email}
                        </div>
                        <div className="text-xs font-mono text-[#5B9BD5]">
                          {emp.itsNo}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">
                          HR Type
                        </div>
                        <Badge
                          className={
                            getHRTypeBadgeClass(emp.hrType) + " text-xs"
                          }
                        >
                          {emp.hrType}
                        </Badge>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Status</div>
                        <Badge
                          className={
                            getStatusBadgeClass(emp.status) + " text-xs"
                          }
                        >
                          {emp.status}
                        </Badge>
                      </div>
                      {/* <div>
                        <div className="text-xs text-gray-500 mb-1">Venue</div>
                        <div className="text-xs text-gray-700">{emp.venue} · {emp.subVenue}</div>
                      </div> */}
                      {/* <div>
                        <div className="text-xs text-gray-500 mb-1">Department</div>
                        <div className="text-xs text-gray-700">{emp.department}</div>
                      </div> */}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/employee-management/view/${emp.id}`)
                        }
                        className="flex-1 h-9 text-xs"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/employee-management/edit/${emp.id}`)
                        }
                        className="flex-1 h-9 text-xs"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          {totalCount > pageSize && (
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="employees"
            />
          )}
        </Card>
      </div>
    </>
  );
}
